import React, { useContext, useEffect, useState } from "react";

import {
  Button,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
  Box,
  Chip,
  Tooltip,
} from "@material-ui/core";

import { makeStyles, useTheme } from "@material-ui/core/styles";

import MainContainer from "../../components/MainContainer";
import MainHeader from "../../components/MainHeader";
import MainHeaderButtonsWrapper from "../../components/MainHeaderButtonsWrapper";
import TableRowSkeleton from "../../components/TableRowSkeleton";
import Title from "../../components/Title";
import GuideOpenButton from "../../components/GuideOpenButton";
import { i18n } from "../../translate/i18n";
import toastError from "../../errors/toastError";
import api from "../../services/api";
import {
  DeleteOutline,
  Edit,
  Android,
  Add,
  Memory,
  FlashOn,
  Tune,
  ChatBubbleOutline,
} from "@material-ui/icons";
import PromptModal from "../../components/PromptModal";
import { toast } from "react-toastify";
import ConfirmationModal from "../../components/ConfirmationModal";
import {
  useViewMode,
  ViewModeToggle,
  RecordsCardsGrid,
  RecordCard,
  RecordCardMeta,
} from "../../components/ViewModeToggle";
import { AuthContext } from "../../context/Auth/AuthContext";

import { SocketContext } from "../../context/Socket/SocketContext";
import { getProviderLabel } from "../../constants/aiProviderCatalog";

const useStyles = makeStyles((theme) => ({
  // ✅ CORREGIDO: MainPaper dinámico según tema
  mainPaper: {
    flex: 1,
    padding: theme.spacing(2),
    overflowY: "scroll",
    ...theme.scrollbarStyles,
    borderRadius: 12,
    boxShadow: theme.mode === "dark" ? "0 4px 20px rgba(0,0,0,0.3)" : "0 4px 20px rgba(0,0,0,0.1)",
    backgroundColor: theme.palette.background.paper,
    border: `1px solid ${theme.mode === "dark" ? "#4B5563" : "#e0e0e0"}`,
  },

  // ✅ CORREGIDO: TableHeader dinámico según tema
  tableHeader: {
    backgroundColor: theme.mode === "dark" ? "#374151" : "#f8f9fa",
    "& .MuiTableCell-head": {
      fontWeight: "bold",
      fontSize: "14px",
      color: theme.palette.text.primary,
      borderBottom: `2px solid ${theme.mode === "dark" ? "#4B5563" : "#dee2e6"}`,
    },
  },

  // ✅ CORREGIDO: TableRow dinámico según tema
  tableRow: {
    backgroundColor: theme.palette.background.paper,
    "&:hover": {
      backgroundColor: theme.mode === "dark" ? "#374151" : "#f8f9fa",
      transition: "background-color 0.2s ease",
    },
    "&:nth-of-type(even)": {
      backgroundColor: theme.mode === "dark" ? "#374151" : "#fafbfc",
    },
    "& td": {
      color: theme.palette.text.primary,
      borderBottom: `1px solid ${theme.mode === "dark" ? "#4B5563" : "rgba(224, 224, 224, 0.5)"}`,
    },
  },

  // ✅ ESTILOS PARA BOTONES DE ACCIÓN
  actionButton: {
    margin: "0 4px",
    padding: "8px",
    borderRadius: "8px",
    transition: "all 0.2s ease",
    "&:hover": {
      transform: "scale(1.1)",
    },
  },

  editButton: {
    backgroundColor: theme.mode === "dark" ? "rgba(25, 118, 210, 0.2)" : "#e3f2fd",
    color: "#1976d2",
    "&:hover": {
      backgroundColor: theme.mode === "dark" ? "rgba(25, 118, 210, 0.3)" : "#bbdefb",
    },
  },

  deleteButton: {
    backgroundColor: theme.mode === "dark" ? "rgba(211, 47, 47, 0.2)" : "#ffebee",
    color: "#d32f2f",
    "&:hover": {
      backgroundColor: theme.mode === "dark" ? "rgba(211, 47, 47, 0.3)" : "#ffcdd2",
    },
  },

  // ✅ ESTILOS PARA CHIPS DE INFORMACIÓN (mantener colores vibrantes)
  providerChip: {
    backgroundColor: "#e8f5e8",
    color: "#2e7d32",
    fontWeight: "bold",
    fontSize: "12px",
  },

  tokensChip: {
    backgroundColor: "#fff3e0",
    color: "#f57c00",
    fontWeight: "bold",
    fontSize: "12px",
  },

  temperatureChip: {
    backgroundColor: "#e3f2fd",
    color: "#1976d2",
    fontWeight: "bold",
    fontSize: "12px",
  },

  messagesChip: {
    backgroundColor: "#f3e5f5",
    color: "#7b1fa2",
    fontWeight: "bold",
    fontSize: "12px",
  },

  // ✅ ESTILOS PARA EL HEADER CON ICONO DE IA
  aiHeader: {
    display: "flex",
    alignItems: "center",
    "& .MuiSvgIcon-root": {
      marginRight: theme.spacing(1),
      color: theme.palette.primary.main,
    },
  },

  // ✅ ESTILOS PARA EL BOTÓN AGREGAR
  addButton: {
    background: "linear-gradient(45deg, #1976d2 30%, #42a5f5 90%)",
    borderRadius: "8px",
    padding: "12px 24px",
    fontWeight: "bold",
    textTransform: "none",
    boxShadow: "0 4px 15px rgba(25, 118, 210, 0.3)",
    "&:hover": {
      background: "linear-gradient(45deg, #1565c0 30%, #1976d2 90%)",
      transform: "translateY(-2px)",
      boxShadow: "0 6px 20px rgba(25, 118, 210, 0.4)",
    },
  },

  // ✅ CORREGIDO: InfoBox dinámico según tema
  infoBox: {
    backgroundColor: theme.mode === "dark" ? "#1e3a5f" : "#f0f8ff",
    border: `1px solid ${theme.mode === "dark" ? "#2d4f7a" : "#e3f2fd"}`,
    borderRadius: "8px",
    padding: theme.spacing(2),
    marginBottom: theme.spacing(2),
  },

  // ✅ CORREGIDO: InfoText dinámico según tema
  infoText: {
    color: theme.palette.primary.main,
    fontSize: "14px",
    fontWeight: "500",
  },

  // ✅ CORREGIDO: LinkText dinámico según tema
  linkText: {
    color: theme.palette.primary.main,
    textDecoration: "none",
    fontWeight: "bold",
    "&:hover": {
      textDecoration: "underline",
    },
  },
}));

const Prompts = () => {
  const classes = useStyles();
  const theme = useTheme();

  const [prompts, setPrompts] = useState([]);
  const [loading, setLoading] = useState(false);

  const [promptModalOpen, setPromptModalOpen] = useState(false);
  const [selectedPrompt, setSelectedPrompt] = useState(null);
  const [confirmModalOpen, setConfirmModalOpen] = useState(false);
  const [viewMode, setViewMode] = useViewMode("prompts");
  const { user } = useContext(AuthContext);
  const companyId = user.companyId;

  const socketManager = useContext(SocketContext);

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const { data } = await api.get("/prompt");
        setPrompts(data.prompts);
        setLoading(false);
      } catch (err) {
        toastError(err);
        setLoading(false);
      }
    })();
  }, []);

  useEffect(() => {
    const socket = socketManager.getSocket(companyId);

    socket.on(`company-${companyId}-prompt`, (data) => {
      if (data.action === "update" || data.action === "create") {
        setPrompts((prevPrompts) => {
          const existingIndex = prevPrompts.findIndex((p) => p.id === data.prompt.id);
          if (existingIndex !== -1) {
            const newPrompts = [...prevPrompts];
            newPrompts[existingIndex] = data.prompt;
            return newPrompts;
          } else {
            return [data.prompt, ...prevPrompts];
          }
        });
      }

      if (data.action === "delete") {
        setPrompts((prevPrompts) => prevPrompts.filter((p) => p.id !== data.promptId));
      }
    });

    return () => {
      socket.disconnect();
    };
  }, [companyId, socketManager]);

  const handleOpenPromptModal = () => {
    setPromptModalOpen(true);
    setSelectedPrompt(null);
  };

  const handleClosePromptModal = () => {
    setPromptModalOpen(false);
    setSelectedPrompt(null);
  };

  const handleEditPrompt = (prompt) => {
    setSelectedPrompt(prompt);
    setPromptModalOpen(true);
  };

  const handleCloseConfirmationModal = () => {
    setConfirmModalOpen(false);
    setSelectedPrompt(null);
  };

  const handleDeletePrompt = async (promptId) => {
    try {
      await api.delete(`/prompt/${promptId}`);
      toast.success("Agente IA eliminado");

      setPrompts((prevPrompts) => prevPrompts.filter((p) => p.id !== promptId));
    } catch (err) {
      toastError(err);
    }
    setSelectedPrompt(null);
    setConfirmModalOpen(false);
  };

  // ✅ FUNCIÓN PARA ACTUALIZAR LA LISTA DESPUÉS DE GUARDAR
  const handleRefreshPrompts = async () => {
    try {
      const { data } = await api.get("/prompt");
      setPrompts(data.prompts);
    } catch (err) {
      toastError(err);
    }
  };

  return (
    <MainContainer>
      <ConfirmationModal
        title={
          selectedPrompt
            ? i18n.t("prompts.confirmationModal.deleteTitleNamed", {
                name: selectedPrompt.name,
              })
            : ""
        }
        open={confirmModalOpen}
        onClose={handleCloseConfirmationModal}
        onConfirm={() => handleDeletePrompt(selectedPrompt.id)}
      >
        {i18n.t("prompts.confirmationModal.deleteMessage")}
      </ConfirmationModal>

      <PromptModal
        open={promptModalOpen}
        onClose={handleClosePromptModal}
        promptId={selectedPrompt?.id}
        onSave={() => {
          handleRefreshPrompts();
          handleClosePromptModal();
        }}
      />

      <MainHeader>
        <Title>
          <Box className={classes.aiHeader}>
            <Memory style={{ fontSize: 28 }} />
            <Typography
              variant="h4"
              style={{ fontWeight: "bold", color: theme.palette.primary.main }}
            >
              {i18n.t("prompts.listPage.title")}
            </Typography>
            <GuideOpenButton id="ai" title={i18n.t("prompts.listPage.title")} to="/prompts" startId="agent" />
          </Box>
        </Title>
        <MainHeaderButtonsWrapper>
          <Button
            variant="contained"
            className={classes.addButton}
            onClick={handleOpenPromptModal}
            startIcon={<Add />}
          >
            {i18n.t("prompts.listPage.addButton")}
          </Button>
          <ViewModeToggle value={viewMode} onChange={setViewMode} />
        </MainHeaderButtonsWrapper>
      </MainHeader>

      {/* ✅ INFORMACIÓN SOBRE PROVEEDORES DE IA */}
      <Paper className={classes.infoBox}>
        <Box display="flex" alignItems="center" marginBottom={1}>
          <Android style={{ marginRight: 8, color: theme.palette.primary.main }} />
          <Typography
            variant="h6"
            style={{ color: theme.palette.primary.main, fontWeight: "bold" }}
          >
            {i18n.t("prompts.listPage.providersTitle")}
          </Typography>
        </Box>
        <Typography className={classes.infoText}>
          {i18n.t("prompts.listPage.providersIntro")}
        </Typography>
        <Typography className={classes.infoText} style={{ marginTop: 8 }}>
          {i18n.t("prompts.listPage.providersApiKey")}
        </Typography>
      </Paper>

      <Paper className={classes.mainPaper} variant="outlined">
        {viewMode === "cards" ? (
          <RecordsCardsGrid>
            {prompts.map((prompt) => (
              <RecordCard
                key={`prompt-card-${prompt.id}`}
                title={prompt.name}
                actions={
                  <>
                    <IconButton size="small" className={`${classes.actionButton} ${classes.editButton}`} onClick={() => handleEditPrompt(prompt)}>
                      <Edit />
                    </IconButton>
                    <IconButton size="small" className={`${classes.actionButton} ${classes.deleteButton}`} onClick={() => { setSelectedPrompt(prompt); setConfirmModalOpen(true); }}>
                      <DeleteOutline />
                    </IconButton>
                  </>
                }
              >
                <RecordCardMeta>{getProviderLabel(prompt.provider)}</RecordCardMeta>
                <RecordCardMeta>
                  {i18n.t("prompts.listPage.tokensChip", { count: prompt.maxTokens || 400 })}
                  {" · "}
                  {prompt.temperature !== null && prompt.temperature !== undefined ? prompt.temperature : "0.5"}
                </RecordCardMeta>
              </RecordCard>
            ))}
          </RecordsCardsGrid>
        ) : (
        <Table size="small">
          <TableHead className={classes.tableHeader}>
            <TableRow>
              <TableCell align="left">
                <Box display="flex" alignItems="center">
                  <Memory style={{ marginRight: 8, fontSize: 20 }} />
                  <strong>{i18n.t("prompts.listPage.tablePromptName")}</strong>
                </Box>
              </TableCell>
              <TableCell align="left">
                <Box display="flex" alignItems="center">
                  <Android style={{ marginRight: 8, fontSize: 20 }} />
                  <strong>{i18n.t("prompts.listPage.tableProvider")}</strong>
                </Box>
              </TableCell>
              <TableCell align="left">
                <Box display="flex" alignItems="center">
                  <FlashOn style={{ marginRight: 8, fontSize: 20 }} />
                  <strong>{i18n.t("prompts.listPage.tableMaxTokens")}</strong>
                </Box>
              </TableCell>
              <TableCell align="left">
                <Box display="flex" alignItems="center">
                  <Tune style={{ marginRight: 8, fontSize: 20 }} />
                  <strong>{i18n.t("prompts.listPage.tableTemperature")}</strong>
                </Box>
              </TableCell>
              <TableCell align="left">
                <Box display="flex" alignItems="center">
                  <ChatBubbleOutline style={{ marginRight: 8, fontSize: 20 }} />
                  <strong>{i18n.t("prompts.listPage.tableMaxMessages")}</strong>
                </Box>
              </TableCell>
              <TableCell align="center">
                <strong>{i18n.t("prompts.listPage.tableActions")}</strong>
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            <>
              {prompts.map((prompt) => (
                <TableRow key={prompt.id} className={classes.tableRow}>
                  <TableCell align="left">
                    <Box display="flex" alignItems="center">
                      <Memory style={{ marginRight: 8, color: "#1976d2" }} />
                      <Typography style={{ fontWeight: "500" }}>{prompt.name}</Typography>
                    </Box>
                  </TableCell>
                  <TableCell align="left">
                    <Chip
                      label={getProviderLabel(prompt.provider)}
                      className={classes.providerChip}
                      size="small"
                    />
                  </TableCell>
                  <TableCell align="left">
                    <Chip
                      label={i18n.t("prompts.listPage.tokensChip", {
                        count: prompt.maxTokens || 400,
                      })}
                      className={classes.tokensChip}
                      size="small"
                    />
                  </TableCell>
                  <TableCell align="left">
                    <Chip
                      label={
                        prompt.temperature !== null && prompt.temperature !== undefined
                          ? prompt.temperature
                          : "0.5"
                      }
                      className={classes.temperatureChip}
                      size="small"
                    />
                  </TableCell>
                  <TableCell align="left">
                    <Chip
                      label={prompt.maxMessages || 25}
                      className={classes.messagesChip}
                      size="small"
                    />
                  </TableCell>
                  <TableCell align="center">
                    <Tooltip title={i18n.t("prompts.listPage.tooltipEdit")}>
                      <IconButton
                        size="small"
                        className={`${classes.actionButton} ${classes.editButton}`}
                        onClick={() => handleEditPrompt(prompt)}
                      >
                        <Edit />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title={i18n.t("prompts.listPage.tooltipDelete")}>
                      <IconButton
                        size="small"
                        className={`${classes.actionButton} ${classes.deleteButton}`}
                        onClick={() => {
                          setSelectedPrompt(prompt);
                          setConfirmModalOpen(true);
                        }}
                      >
                        <DeleteOutline />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
              {loading && <TableRowSkeleton columns={6} />}
            </>
          </TableBody>
        </Table>
        )}
      </Paper>
    </MainContainer>
  );
};

export default Prompts;
